import * as React from 'react'

import { CalendarOff, Copy, Ellipsis, Link, Trash2 } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import commonRoutes, {
  getTransitionPath,
  getURLParam,
} from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import DeliveryFollowingsStore from '~/client/src/shared/stores/domain/DeliveryFollowings.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { toast } from '~/client/src/shared/utils/toast'

import DeliveryAssociationStatus from '../../../DeliveryAssociationStatus/DeliveryAssociationStatus'
import DeliveryDetailsStore from '../../DeliveryDetails.store'

// translated

interface IProps {
  deliveryDetailsStore: DeliveryDetailsStore
  deliveryFollowingsStore?: DeliveryFollowingsStore
  state?: InitialState
}

const {
  duplicateCurrentDelivery,
  unsubscribeToDelivery,
  copyLinkToDelivery,
  subscribeToDelivery,
  deleteDelivery,
  copiedToClipboard,
  checkInUnscheduledDelivery,
  workflowMenu,
} = Localization.translator

@inject('deliveryFollowingsStore', 'state')
@observer
export default class DeliveryWorkflowMoreMenu extends React.Component<IProps> {
  public render() {
    const {
      deliveryFollowingsStore: { isEntityFollowed },
      deliveryDetailsStore: {
        displayedDelivery,
        canDeleteDelivery,
        canDuplicateDelivery,
        toggleUnscheduledDelivery,
        shouldShowUnscheduledControl,
        isUnscheduledDelivery,
      },
    } = this.props

    const itemClassName = 'tw:gap-2 tw:text-body-md'

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={workflowMenu}
              className="tw:text-foreground"
            />
          }
        >
          <Ellipsis aria-hidden="true" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="tw:w-[240px]">
          {canDuplicateDelivery && (
            <DropdownMenuItem
              className={itemClassName}
              onClick={this.duplicateDelivery}
            >
              <Copy />
              {duplicateCurrentDelivery}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className={itemClassName}
            onClick={this.toggleEntityFollowing}
          >
            <DeliveryAssociationStatus
              isBigStarMode={true}
              deliveryId={displayedDelivery.id}
            />
            {isEntityFollowed(displayedDelivery.id)
              ? unsubscribeToDelivery
              : subscribeToDelivery}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClassName}
            onClick={this.copyDeliveryLink}
          >
            <Link />
            {copyLinkToDelivery}
          </DropdownMenuItem>
          {shouldShowUnscheduledControl && (
            <DropdownMenuItem
              closeOnClick={false}
              className={classList({
                [itemClassName]: true,
                'tw:text-primary': isUnscheduledDelivery,
              })}
              onClick={toggleUnscheduledDelivery}
            >
              <CalendarOff />
              {checkInUnscheduledDelivery}
            </DropdownMenuItem>
          )}
          {canDeleteDelivery && (
            <DropdownMenuItem
              variant="destructive"
              className={itemClassName}
              onClick={this.deleteDelivery}
            >
              <Trash2 />
              {deleteDelivery}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  private toggleEntityFollowing = () => {
    const {
      deliveryFollowingsStore: { toggleEntityFollowing },
      deliveryDetailsStore: { displayedDelivery },
    } = this.props

    toggleEntityFollowing(displayedDelivery.id)
  }

  private duplicateDelivery = () => {
    this.props.deliveryDetailsStore.duplicateDelivery()
  }

  private deleteDelivery = () => {
    const { canDeleteDelivery, onDeleteAction } =
      this.props.deliveryDetailsStore

    if (!canDeleteDelivery) {
      return
    }

    onDeleteAction()
  }

  private copyDeliveryLink = () => {
    const {
      deliveryDetailsStore: { displayedDelivery },
    } = this.props

    const path = getTransitionPath(
      commonRoutes.DELIVERY_DETAILS.replace(
        getURLParam(commonRoutes.DELIVERY_DETAILS),
        displayedDelivery.id,
      ),
      this.props.state.activeOrInitProjectCode,
    )

    navigator.clipboard.writeText(location.origin + path)
    toast.success(copiedToClipboard)
  }
}
